import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin } from 'lucide-react';

const CalendarioReservas = ({ onClose }) => {
  const { user } = useAuth();
  const { getCondominioId } = useCondominio();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reservas, setReservas] = useState([]);
  const [areasComuns, setAreasComuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reservasDoDia, setReservasDoDia] = useState([]);

  useEffect(() => {
    loadReservas();
    loadAreasComuns();
  }, [currentDate]);

  const loadReservas = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await api.get(`/reservas?condominio_id=${condominioId}&data_inicio=${startDate.toISOString()}&data_fim=${endDate.toISOString()}`);
      setReservas(response.data.data.reservas || []);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAreasComuns = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) return;
      
      const response = await api.get(`/areas-comuns?condominio_id=${condominioId}`);
      setAreasComuns(response.data.data.areasComuns || []);
    } catch (error) {
      console.error('Erro ao carregar áreas comuns:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Adicionar dias vazios do mês anterior
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Adicionar dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getReservasDoDia = (date) => {
    if (!date) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    return reservas.filter(reserva => {
      const reservaDate = new Date(reserva.reserva_data_inicio).toISOString().split('T')[0];
      return reservaDate === dateStr;
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    setSelectedDate(date);
    setReservasDoDia(getReservasDoDia(date));
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
    setReservasDoDia([]);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pendente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'confirmada': 'bg-green-100 text-green-800 border-green-200',
      'em_andamento': 'bg-blue-100 text-blue-800 border-blue-200',
      'concluida': 'bg-gray-100 text-gray-800 border-gray-200',
      'cancelada': 'bg-red-100 text-red-800 border-red-200',
      'rejeitada': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pendente': 'Pendente',
      'confirmada': 'Confirmada',
      'em_andamento': 'Em Andamento',
      'concluida': 'Concluída',
      'cancelada': 'Cancelada',
      'rejeitada': 'Rejeitada'
    };
    return labels[status] || status;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Calendário de Reservas</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Calendário */}
          <div className="flex-1 p-6">
            {/* Navegação do mês */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h3 className="text-lg font-semibold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Dias da semana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Dias do mês */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dayReservas = day ? getReservasDoDia(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && day && day.toDateString() === selectedDate.toDateString();
                
                return (
                  <div
                    key={index}
                    className={`
                      min-h-[80px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50
                      ${!day ? 'bg-gray-50' : ''}
                      ${isToday ? 'bg-blue-50 border-blue-300' : ''}
                      ${isSelected ? 'bg-blue-100 border-blue-400' : ''}
                    `}
                    onClick={() => handleDateClick(day)}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                          {day.getDate()}
                        </div>
                        {dayReservas.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {dayReservas.slice(0, 2).map(reserva => (
                              <div
                                key={reserva.reserva_id}
                                className={`text-xs px-1 py-0.5 rounded border ${getStatusColor(reserva.reserva_status)}`}
                                title={`${reserva.area_comum?.area_comum_nome || 'N/A'} - ${formatTime(reserva.reserva_data_inicio)}`}
                              >
                                {formatTime(reserva.reserva_data_inicio)}
                              </div>
                            ))}
                            {dayReservas.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{dayReservas.length - 2} mais
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Painel de detalhes */}
          <div className="w-96 border-l bg-gray-50 p-6 overflow-y-auto">
            {selectedDate ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Reservas de {selectedDate.toLocaleDateString('pt-BR')}
                </h3>
                
                {reservasDoDia.length > 0 ? (
                  <div className="space-y-3">
                    {reservasDoDia.map(reserva => (
                      <div
                        key={reserva.reserva_id}
                        className={`p-3 rounded-lg border ${getStatusColor(reserva.reserva_status)}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span className="font-medium text-sm">
                              {reserva.area_comum?.area_comum_nome || 'N/A'}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(reserva.reserva_status)}`}>
                            {getStatusLabel(reserva.reserva_status)}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatTime(reserva.reserva_data_inicio)}</span>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <strong>Apartamento:</strong> {reserva.apartamento?.apartamento_bloco || 'N/A'} - {reserva.apartamento?.apartamento_numero || 'N/A'}
                        </div>
                        
                        {reserva.reserva_motivo && (
                          <div className="text-sm text-gray-600 mt-1">
                            <strong>Motivo:</strong> {reserva.reserva_motivo}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhuma reserva para este dia</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Clique em uma data para ver as reservas</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarioReservas;



